import { useAuth } from "@clerk/clerk-react";
import { Listbox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { TemplateObject } from "@neum/frontend-shared/types/templateTypes";
import { asError } from "@neum/frontend-shared/utils/error";
import { createDocumentSpecification } from "@neum/frontend-shared/utils/metagraphUtils";
import {
  isPhoneError,
  type PhoneStatus,
} from "@neum/frontend-shared/utils/phoneUtils";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecording } from "../../providers/RecordingProvider";
import { processDocuments } from "../../serverActions/sessionMetagraphApis";
import { useSupabase } from "../../supabase/SupabaseContext";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import ErrorAlert from "../Error";
import PhoneDialingAnimation from "./PhoneDialingAnimation";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";

export interface PhoneRecordingPanelProps {
  selectedLanguage: string;
  template: TemplateObject | undefined;
}

const processingButtonText: Record<PhoneStatus, string> = {
  call_connected: "Call in progress...",
  call_ended: "Preparing recording...",
  call_failed: "Call failed",
  dialing_clinic: "Starting call...",
  dialing_clinic_failed: "Unable to start call",
  dialing_patient: "Connecting to your patient...",
  dialing_patient_failed: "Unable to connect to your patient",
  idle: "Call not started",
  recording_failed: "Unable to process recording",
  recording_ready: "Generate notes",
  timeout: "Unknown error",
  unknown: "Unknown error",
};

const errorText: Record<PhoneStatus, string> = {
  call_connected: "",
  call_ended: "",
  call_failed: "Call failed",
  dialing_clinic: "",
  dialing_clinic_failed: "Unable to start call",
  dialing_patient: "",
  dialing_patient_failed: "Unable to connect to your patient",
  idle: "Call not started",
  recording_failed: "Unable to process recording",
  recording_ready: "",
  timeout: "Unknown error",
  unknown: "Unknown error",
};

export function PhoneRecordingPanel({
  selectedLanguage,
  template,
}: PhoneRecordingPanelProps) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [toPhoneNumber, setToPhoneNumber] = useState<string>("");
  const [toPhoneNumberValid, setToPhoneNumberValid] = useState<boolean>(false);
  const {
    sessionId,
    recordingId,
    documentId,
    startRecording,
    fromPhoneNumber,
    updateFromPhoneNumber,
    verifiedPhoneNumbers,
    verifiedPhoneNumbersLoading,
    petNames,
  } = useRecording();
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>("idle");
  const [error, setError] = useState<string | undefined>("");
  const { subscribeToPhoneStream, removePhoneSubscriptions } = useSupabase();
  useEffect(() => {
    subscribeToPhoneStream(
      sessionId,
      recordingId,
      (status) => {
        setPhoneStatus(status);
        if (isPhoneError(status)) {
          setError(errorText[status]);
        }
      },
      () => {}
    ).catch((error: unknown) => {
      setError("Unable to get status of phone call: " + asError(error).message);
    });
    return () => {
      void removePhoneSubscriptions();
    };
  }, [
    sessionId,
    recordingId,
    subscribeToPhoneStream,
    removePhoneSubscriptions,
  ]);

  const templateId = template
    ? `${template.organization}/${template.id}`
    : undefined;

  const startCallHandler = () => {
    if (templateId) {
      startRecording({
        pets: petNames,
        templateId,
        type: "phone",
        reset: true,
        toPhoneNumber,
      }).catch((e: unknown) => {
        setError("Unable to start phone call: " + asError(e).message);
      });
    }
  };
  const processButtonClick = useCallback(async () => {
    const token = (await getToken({ template: "supabase" })) ?? "";
    await processDocuments({
      token,
      body: {
        session_id: sessionId,
        session_spoken_language: selectedLanguage,
        process_type: "create",
        process_command: "process_all_required",
        specifications: [
          createDocumentSpecification({
            node_id: documentId,
            template_id: templateId,
          }),
        ],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
    navigate("/history?session_id=" + sessionId + "&tab=notes");
  }, [templateId, getToken, documentId, sessionId, selectedLanguage, navigate]);

  const fromPhoneNumberText = verifiedPhoneNumbersLoading
    ? "Loading..."
    : fromPhoneNumber
    ? formatPhoneNumber(
        fromPhoneNumber.phone_number,
        fromPhoneNumber.country_code
      )
    : "Select a number";

  return (
    <div className="grid grid-cols-3 grid-rows-1">
      <div className="col-span-1 row-span-1 col-start-1 row-start-1 flex flex-col align-middle height-full justify-center">
        <div className="shrink-0 w-60 h-12 relative">
          <label
            htmlFor="phone"
            className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-main-text-darker z-[1] rounded-md"
          >
            From:
          </label>
          <Listbox value={fromPhoneNumber} onChange={updateFromPhoneNumber}>
            <div className="relative z-[0]">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm h-12">
                <span className="block truncate">{fromPhoneNumberText}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute mt-1 max-h-36 w-full overflow-auto thin-scrollbar rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm cursor-pointer z-[1]">
                  {verifiedPhoneNumbers.map((phoneNumber) => {
                    const formattedPhoneNumber = formatPhoneNumber(
                      phoneNumber.phone_number,
                      phoneNumber.country_code
                    );
                    return (
                      <Listbox.Option
                        key={JSON.stringify(formattedPhoneNumber)}
                        value={phoneNumber}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active
                              ? "bg-accent text-main-text-darker"
                              : "text-main-text-darker"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <div
                            className={`flex flex-col truncate ${
                              selected ? "font-medium" : "font-normal"
                            }`}
                          >
                            <div className="truncate">
                              {phoneNumber.label || formattedPhoneNumber}
                            </div>
                            {phoneNumber.label && (
                              <div className="truncate">
                                {formattedPhoneNumber}
                              </div>
                            )}
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-accent">
                                <CheckIcon
                                  className="h-5 w-5 fill-main"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>
      </div>
      <div className="col-span-1 row-span-1 row-start-1 col-start-2 px-8 flex-col justify-center items-center">
        {phoneStatus === "idle" ||
        phoneStatus === "call_ended" ||
        phoneStatus === "recording_ready" ? (
          <button
            type="button"
            onClick={startCallHandler}
            className={`rounded-full bg-main-button hover:bg-main-button-hover px-4 py-2.5 text-md font-semibold text-main-button-text shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-64 w-64 flex flex-col justify-center justify-items-center items-center gap-y-4`}
          >
            <span className="text-white text-xl font-semibold">Start Call</span>
            <PhoneIcon className="h-10" />
          </button>
        ) : (
          <>
            <PhoneDialingAnimation
              width="200"
              height="200"
              isDialing={
                phoneStatus === "dialing_clinic" ||
                phoneStatus === "dialing_patient"
              }
            />
            <div className="mt-4 text-center">
              {phoneStatus === "dialing_clinic" && (
                <p className="text-main-text-darker">Dialing clinic...</p>
              )}
              {phoneStatus === "dialing_patient" && (
                <p className="text-main-text-darker">Dialing patient...</p>
              )}
              {phoneStatus === "call_connected" && (
                <p className="text-green-600">Call connected</p>
              )}
              {phoneStatus === "dialing_clinic_failed" && (
                <p className="text-red-600">Failed to dial clinic</p>
              )}
              {phoneStatus === "dialing_patient_failed" && (
                <p className="text-red-600">Failed to dial patient</p>
              )}
              {phoneStatus === "call_failed" && (
                <p className="text-red-600">Call failed</p>
              )}
              {phoneStatus === "recording_failed" && (
                <p className="text-red-600">Recording failed</p>
              )}
              {phoneStatus === "timeout" && (
                <p className="text-red-600">Call timed out</p>
              )}
              {phoneStatus === "unknown" && (
                <p className="text-red-600">Unknown error</p>
              )}
            </div>
          </>
        )}
        <button
          type="button"
          className={`inline-flex items-center justify-center gap-x-2 rounded-md my-4 ${
            phoneStatus === "recording_ready"
              ? "bg-main-button hover:bg-main-button-hover text-main-button-text"
              : "bg-main-button-disabled text-main-button-disabled-text"
          } px-3.5 py-2.5 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-12 w-60 justify-center`}
          onClick={processButtonClick}
          disabled={phoneStatus !== "recording_ready"}
        >
          {processingButtonText[phoneStatus]}
        </button>
      </div>
      <div className="col-span-2 row-span-1 row-start-1 col-start-3 flex flex-col height-full justify-center place-items-end">
        <div className="shrink-0 w-60 h-12 relative">
          <label className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-main-text-darker z-[1] rounded-md">
            To:
          </label>
          <IntlTelInput
            initialValue={toPhoneNumber}
            onChangeNumber={setToPhoneNumber}
            onChangeValidity={setToPhoneNumberValid}
            initOptions={{ initialCountry: "us" }}
            inputProps={{ className: !!toPhoneNumber && !toPhoneNumberValid ? "border-red-600" : "" }}
           />
          {!!toPhoneNumber && !toPhoneNumberValid && (
            <p className="text-red-600 text-sm mt-1">
              Invalid phone number
            </p>
          )}
        </div>
      </div>
      {error && <ErrorAlert error={error} setError={setError} />}
    </div>
  );
}
